/**
 * The attribute builder — the matrix's axis editor, per the TradeCore mockup:
 * each attribute is a name input + chip values + trash; quick-add seeds common
 * names; "+ Add Attribute" appends a blank row.
 *
 * Fully controlled by the form (`value`/`onChange`, the `pricing-entry-list`
 * contract) — it owns no list state, so RHF's dirty tracking and the save
 * payload both see every edit.
 */
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@flint/ui";
import type { VariantAttribute } from "@flint/domain/commerce/store/variant-matrix";
import { MAX_ATTRIBUTES } from "@flint/domain/commerce/store/variant-matrix";
import { ChipInput } from "./chip-input.tsx";

/** The mockup's quick-add vocabulary, verbatim. */
const QUICK_ADD = [
  "Size",
  "Color",
  "Material",
  "Fabric",
  "Weight",
  "Volume",
  "Storage",
  "Flavor",
] as const;

export interface AttributeBuilderProps {
  value: VariantAttribute[];
  onChange: (next: VariantAttribute[]) => void;
}

export function AttributeBuilder({ value, onChange }: AttributeBuilderProps) {
  const atCap = value.length >= MAX_ATTRIBUTES;
  const usedNames = new Set(value.map((a) => a.name.trim().toLowerCase()));

  const patch = (i: number, next: Partial<VariantAttribute>) =>
    onChange(value.map((a, idx) => (idx === i ? { ...a, ...next } : a)));

  return (
    <div className="flex flex-col gap-3">
      {value.length === 0 ? (
        <div className="rounded-sm border border-dashed border-line px-4 py-6 text-center">
          <p className="text-sm text-ink">No attributes yet.</p>
          <p className="mt-1 text-xs text-muted">
            Add one below — every combination of values becomes a variant.
          </p>
        </div>
      ) : (
        value.map((attr, i) => (
          <div
            // Index-keyed on purpose: the name is editable, so it can't be the
            // key without remounting (and blurring) the input on every keystroke.
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className="flex flex-col gap-2 rounded-sm border border-line bg-paper px-3.5 py-3"
          >
            <div className="flex items-center gap-2">
              <input
                value={attr.name}
                onChange={(e) => patch(i, { name: e.target.value })}
                placeholder="Attribute name — e.g. Size"
                aria-label={`Attribute ${i + 1} name`}
                maxLength={48}
                className="h-9 flex-1 rounded-sm border border-line bg-paper px-3 text-sm text-ink transition-colors placeholder:text-placeholder hover:border-line-2 focus-visible:border-sky-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-100"
              />
              <button
                type="button"
                aria-label={`Remove attribute ${attr.name || i + 1}`}
                className="rounded-sm p-1.5 text-muted transition-colors hover:bg-raised hover:text-bad"
                onClick={() => onChange(value.filter((_, idx) => idx !== i))}
              >
                <Trash2 className="size-4" aria-hidden />
              </button>
            </div>
            <ChipInput
              value={attr.values}
              onChange={(values) => patch(i, { values })}
              max={24}
              aria-label={`${attr.name || `Attribute ${i + 1}`} values`}
            />
          </div>
        ))
      )}

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-muted">Quick add:</span>
        {QUICK_ADD.map((name) => (
          <button
            key={name}
            type="button"
            disabled={atCap || usedNames.has(name.toLowerCase())}
            onClick={() => onChange([...value, { name, values: [] }])}
            className="inline-flex h-7 items-center gap-1 rounded-full border border-line bg-paper px-2.5 text-xs text-ink-2 transition-colors hover:border-line-2 hover:bg-raised disabled:opacity-40"
          >
            <Plus className="size-3" aria-hidden />
            {name}
          </button>
        ))}
      </div>

      <div>
        <Button
          intent="secondary"
          size="md"
          disabled={atCap}
          onClick={() => onChange([...value, { name: "", values: [] }])}
        >
          <Plus size={14} aria-hidden />
          Add Attribute
        </Button>
        {atCap ? (
          <p className="mt-1.5 text-xs text-muted">
            {MAX_ATTRIBUTES} attributes is the maximum for one product.
          </p>
        ) : null}
      </div>
    </div>
  );
}
